import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Tooltip, makeStyles, mergeClasses } from '@fluentui/react-components'
import { ChevronLeft20Regular, ChevronRight20Regular } from '@fluentui/react-icons'
import { useQuery } from '@tanstack/react-query'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'
import { useUnreviewedLandings } from '../hooks/useUnreviewedLandings'
import api from '../lib/api'
import type { DemandListResponse } from '../types/demand'
import type { NotificationListResponse } from '../types/notification'
import type { ProductListResponse } from '../types/product'
import {
  visibleEntriesForRole,
  type BadgeSource,
  type NavBadgeContext,
  type NavRole,
} from './nav-entries'
import {
  sidebarBg,
  sidebarBgHover,
  sidebarBgActive,
  sidebarText,
  sidebarTextActive,
  sidebarBorder,
} from '../theme'

/* ─── Local layout constants ────────────────────────────────────────── */

const COLLAPSE_STORAGE_KEY = 'daccelerate_v2_sidebar_collapsed'
const EXPANDED_WIDTH = '240px'
const COLLAPSED_WIDTH = '48px'
const BRAND_110 = '#54749a' // from theme.ts bayer brand ramp
const BADGE_RED = '#d13438' // Fluent red-fg3 equivalent

/* ─── Styles ────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  root: {
    width: EXPANDED_WIDTH,
    flexShrink: 0,
    background: sidebarBg,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: sidebarBorder,
    display: 'flex',
    flexDirection: 'column',
    color: sidebarText,
    height: '100vh',
    position: 'sticky',
    top: 0,
    transitionProperty: 'width',
    transitionDuration: '200ms',
    transitionTimingFunction: 'cubic-bezier(0.33, 0, 0.67, 1)',
  },
  rootCollapsed: {
    width: COLLAPSED_WIDTH,
  },
  logo: {
    padding: '20px 16px 12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '16px',
  },
  logoMark: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
    flexShrink: 0,
    // Subtle halo so the SVG mark reads cleanly on the dark sidebar
    // chrome without losing the colored fills.
    filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.22)) drop-shadow(0 1px 4px rgba(0,0,0,0.4))',
  },
  logoText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  nav: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '4px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  section: {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#8aa5c2',
    padding: '12px 12px 4px 12px',
    fontWeight: 600,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '7px 12px',
    fontSize: '13px',
    color: sidebarText,
    textDecoration: 'none',
    borderRadius: '4px',
    position: 'relative',
    cursor: 'pointer',
    userSelect: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: '100ms',
    ':hover': {
      backgroundColor: sidebarBgHover,
      color: sidebarTextActive,
    },
  },
  linkCollapsed: {
    justifyContent: 'center',
    padding: '8px',
  },
  linkActive: {
    backgroundColor: sidebarBgActive,
    color: sidebarTextActive,
    fontWeight: 600,
    '::before': {
      content: '""',
      position: 'absolute',
      left: '-8px',
      top: '8px',
      bottom: '8px',
      width: '3px',
      background: BRAND_110,
      borderTopRightRadius: '2px',
      borderBottomRightRadius: '2px',
    },
  },
  linkDisabled: {
    color: '#8aa5c2',
    cursor: 'not-allowed',
    opacity: 0.7,
    ':hover': {
      backgroundColor: 'transparent',
      color: '#8aa5c2',
    },
  },
  navIcon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'inherit',
  },
  navLabel: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  navBadge: {
    marginLeft: 'auto',
    background: BADGE_RED,
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: '999px',
    minWidth: '18px',
    textAlign: 'center',
    lineHeight: '14px',
  },
  navBadgeCollapsed: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    margin: 0,
    fontSize: '9px',
    padding: '0 4px',
    minWidth: '14px',
    lineHeight: '14px',
  },
  footer: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: sidebarBorder,
    padding: '12px',
    flexShrink: 0,
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  // Collapsed rail is only 48px wide — the avatar + toggle can't sit side by
  // side, so stack them vertically and center. Without this the expand button
  // overflowed past the rail edge and became unclickable (couldn't un-collapse).
  footerRowCollapsed: {
    flexDirection: 'column',
    gap: '8px',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #42596e, #6d8b9f)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 600,
    flexShrink: 0,
  },
  avatarTalent: {
    background: 'linear-gradient(135deg, #9F6BC9, #6B2D9E)',
  },
  footerContent: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  footerName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#ffffff',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // #8aa5c2 on the #283744 sidebar is 4.79:1 — the footer's own colour. The
  // old #5a7693 was 2.58:1, below AA for 10px text (#1385).
  versionLine: {
    marginTop: '8px',
    fontSize: '10px',
    color: '#8aa5c2',
    textAlign: 'right',
    letterSpacing: '0.3px',
    userSelect: 'none',
  },
  versionLineCollapsed: {
    marginTop: '6px',
    fontSize: '9px',
    color: '#8aa5c2',
    textAlign: 'center',
    letterSpacing: '0.2px',
    userSelect: 'none',
  },
  footerMeta: {
    fontSize: '10px',
    color: '#8aa5c2',
    marginTop: '1px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  collapseBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '4px',
    color: sidebarText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'background-color, color',
    transitionDuration: '100ms',
    ':hover': {
      background: sidebarBgHover,
      color: sidebarTextActive,
    },
  },
})

/* ─── Helpers ───────────────────────────────────────────────────────── */

function readCollapsedFromStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsedToStorage(value: boolean) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, value ? '1' : '0')
  } catch {
    /* swallow — non-fatal */
  }
}

function initialsFor(name: string | undefined): string {
  if (!name) return '?'
  const parts = name.replace(/^\[TEST\]\s*/i, '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0] ?? ''
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || '?'
  const last = parts[parts.length - 1] ?? first
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?'
}

function roleLabel(
  role: NavRole | undefined | null,
  focusAreaCode?: string | null,
  talentProfileActive?: boolean,
): string {
  if (!role) return ''
  // A member who activated a talent profile is here as talent (basic-user split).
  if (role === 'member') return talentProfileActive ? 'Talent' : 'Member'
  if (role === 'talent') return 'Talent'
  if (role === 'super_admin') return 'Super Admin'
  if (role === 'admin') return focusAreaCode ? `Admin · ${focusAreaCode}` : 'Admin'
  // council — all FA council members are co-leads
  return focusAreaCode ? `Council · ${focusAreaCode} Co-Lead` : 'Council'
}

function resolveBadge(source: BadgeSource | undefined, ctx: NavBadgeContext): number | undefined {
  if (!source) return undefined
  const n = ctx[source]
  if (!n || n <= 0) return undefined
  return n
}

/* ─── Component ─────────────────────────────────────────────────────── */

export function Sidebar() {
  const styles = useStyles()
  const user = useEffectiveUser()
  const role = (user?.role ?? undefined) as NavRole | undefined
  const isCouncilSide = role === 'council' || role === 'admin' || role === 'super_admin'

  const [collapsed, setCollapsed] = useState<boolean>(readCollapsedFromStorage)

  useEffect(() => {
    writeCollapsedToStorage(collapsed)
  }, [collapsed])

  // Live counts for nav badges. Each query is gated by role so we
  // don't issue council-only calls as talent (and vice versa). The
  // count respects the global Show-test-data toggle (exclusive: ON
  // counts only test demands, OFF only real demands) so the badge
  // matches whatever the list/kanban surfaces are showing.
  const { showTestData } = useTestDataVisibility()
  const pendingDecisionsQuery = useQuery<DemandListResponse>({
    queryKey: ['sidebar-pending-decisions', { includeTest: showTestData }],
    queryFn: async () => {
      // Pending decisions = the review band (scoping + triaging). The list
      // endpoint filters a single stage, so fetch both and sum their totals.
      const common = {
        page: 1,
        page_size: 1,
        include_test: showTestData ? 'true' : 'false',
      }
      const [scoping, triaging] = await Promise.all([
        api.get('/demands', { params: { ...common, stage: 'scoping' } }),
        api.get('/demands', { params: { ...common, stage: 'triaging' } }),
      ])
      return {
        ...scoping.data,
        total: (scoping.data?.total ?? 0) + (triaging.data?.total ?? 0),
      }
    },
    enabled: isCouncilSide,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // Notification badge query runs for every logged-in user; the header
  // bell consumes the same data via its own useNotifications hook (also
  // 60s polling). Result is also exposed via badgeCtx so any nav entry
  // can opt into showing the count, though by default no entry does
  // since the bell is the canonical surface.
  const notificationsQuery = useQuery<NotificationListResponse>({
    queryKey: ['sidebar-unread-notifications'],
    queryFn: async () => {
      const res = await api.get('/notifications', { params: { only_unread: true } })
      return res.data
    },
    enabled: !!role,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // A council member is asked about their own focus area; an admin (no focus
  // area) about all of them.
  const unreviewedLandings = useUnreviewedLandings(user?.focus_area_code, isCouncilSide)

  const badgeCtx: NavBadgeContext = {
    pendingDecisions: isCouncilSide ? pendingDecisionsQuery.data?.total : undefined,
    unreviewedLandings: isCouncilSide ? unreviewedLandings.data : undefined,
    unreadNotifications: notificationsQuery.data?.unread,
  }

  // Product-pipeline presence (#1237): the section hides until at least one
  // product exists. The key sits under the ['products'] prefix ON PURPOSE —
  // useCreateProduct / promote invalidate that prefix, so the section appears
  // the moment the first product lands, with no code change here. Fail-closed
  // (hidden while loading or on error); the /products routes stay reachable.
  const productsQuery = useQuery<ProductListResponse>({
    queryKey: ['products', 'nav-presence'],
    queryFn: async () => {
      const res = await api.get('/products', { params: { page: 1, page_size: 1 } })
      return res.data
    },
    enabled: isCouncilSide,
    staleTime: 300_000,
  })

  const entries = visibleEntriesForRole(role, {
    hasProducts: (productsQuery.data?.total ?? 0) > 0,
    talentProfileActive: user?.talent_profile_active ?? false,
    leadsDsaiCluster: !!user?.dsai_cluster_lead,
    viewsAllDemands: user?.views_all_demands ?? false,
  })
  const initials = initialsFor(user?.name)
  const isTalent = role === 'talent' || role === 'member'

  // If the user has a home FA, deep-link "Home" to /focus-areas/{code}
  // so they skip the picker.
  const faCode = user?.focus_area_code
  const decoratedEntries = entries.map((entry) => {
    if (entry.kind === 'link' && entry.to === '/focus-areas' && faCode) {
      return { ...entry, to: `/focus-areas/${faCode}` }
    }
    return entry
  })

  // A parent route (e.g. /demands) must match EXACTLY — otherwise NavLink's
  // default prefix match keeps it highlighted on a child route like
  // /demands/kanban. Treat an entry as a parent when another link entry is
  // nested under it. (Root '/' is always exact.)
  const linkPaths = decoratedEntries.flatMap((e) => (e.kind === 'link' ? [e.to] : []))
  const exactMatch = (to: string) =>
    to === '/' || linkPaths.some((p) => p !== to && p.startsWith(`${to}/`))

  return (
    <aside
      className={mergeClasses(styles.root, collapsed && styles.rootCollapsed)}
      aria-label="Primary navigation"
    >
      <div className={styles.logo}>
        <img src="/dac-logo.svg" alt="" className={styles.logoMark} />
        {!collapsed && <span className={styles.logoText}>DACcelerate</span>}
      </div>

      <nav className={styles.nav}>
        {decoratedEntries.map((entry, idx) => {
          if (entry.kind === 'section') {
            if (collapsed) return null
            return (
              <div key={`section-${idx}-${entry.label}`} className={styles.section}>
                {entry.label}
              </div>
            )
          }
          const badge = resolveBadge(entry.badgeSource, badgeCtx)
          if (entry.disabled) {
            return (
              <Tooltip key={entry.to} content={entry.disabledTooltip ?? 'Coming soon'} relationship="description" withArrow>
                <span
                  className={mergeClasses(styles.link, styles.linkDisabled, collapsed && styles.linkCollapsed)}
                  aria-disabled="true"
                  role="link"
                  tabIndex={0}
                >
                  <span className={styles.navIcon} aria-hidden>
                    {entry.icon}
                  </span>
                  {!collapsed && <span className={styles.navLabel}>{entry.label}</span>}
                </span>
              </Tooltip>
            )
          }
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={exactMatch(entry.to)}
              title={collapsed ? entry.label : undefined}
              className={({ isActive }) =>
                mergeClasses(
                  styles.link,
                  collapsed && styles.linkCollapsed,
                  isActive && styles.linkActive,
                )
              }
            >
              <span className={styles.navIcon} aria-hidden>
                {entry.icon}
              </span>
              {!collapsed && <span className={styles.navLabel}>{entry.label}</span>}
              {badge !== undefined && (
                <span
                  className={mergeClasses(
                    styles.navBadge,
                    collapsed && styles.navBadgeCollapsed,
                  )}
                  aria-label={`${badge} pending`}
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>

      <div className={styles.footer}>
        <div className={mergeClasses(styles.footerRow, collapsed && styles.footerRowCollapsed)}>
          <div
            className={mergeClasses(styles.avatar, isTalent && styles.avatarTalent)}
            aria-hidden
          >
            {initials}
          </div>
          {!collapsed && (
            <div className={styles.footerContent}>
              <div className={styles.footerName} title={user?.name ?? ''}>
                {user?.name ?? 'Guest'}
              </div>
              <div className={styles.footerMeta}>
                {roleLabel(role, user?.focus_area_code, user?.talent_profile_active)}
              </div>
            </div>
          )}
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight20Regular /> : <ChevronLeft20Regular />}
          </button>
        </div>
        <div
          className={collapsed ? styles.versionLineCollapsed : styles.versionLine}
          title={`DACcelerate v${__APP_VERSION__} · built ${__BUILD_TIME__}`}
        >
          v{__APP_VERSION__}
        </div>
      </div>
    </aside>
  )
}
